import { useEffect, useMemo } from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	type DevDocsBylineConnection,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { useAtAGlance, type UseAtAGlanceResult } from '../at-a-glance/useAtAGlance.ts';
import { clearAtAGlance, publishAtAGlance } from '../store/atAGlanceBridge.ts';
import {
	clearDevDocsConnection,
	publishDevDocsConnection,
} from '../store/devDocsConnectionBridge.ts';
import { clearIncomingLinks, publishIncomingLinks } from '../store/incomingLinksBridge.ts';
import { clearOutgoingLinks, publishOutgoingLinks } from '../store/outgoingLinksBridge.ts';
import { clearRelatedContent, publishRelatedContent } from '../store/relatedContentBridge.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

import { useIncomingLinks, type UseIncomingLinksResult } from './useIncomingLinks.ts';
import { useRelatedContent, type UseRelatedContentResult } from './useRelatedContent.ts';
import { useSmartLinks, type UseSmartLinksResult } from './useSmartLinks.ts';

export type BylineConnectionsDataOptions = {
	atAGlanceEnabled: boolean;
	contentId?: string;
	contentType?: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageTitle?: string | null;
};

type BylineConnectionsData = {
	outgoing: UseSmartLinksResult;
	incoming: UseIncomingLinksResult;
	relatedContent: UseRelatedContentResult;
	devDocsConnection: DevDocsBylineConnection | null;
	atAGlance: UseAtAGlanceResult;
};

/** Sole fetching owner, mounted by the byline under its SmartCard provider. */
export const useBylineConnectionsData = ({
	atAGlanceEnabled,
	contentId,
	contentType,
	connectionAncestors,
	pageVersion,
	pageUpdatedAt,
	pageTitle,
}: BylineConnectionsDataOptions): BylineConnectionsData => {
	const outgoing = useSmartLinks(contentId);
	const {
		links: incomingLinks,
		twgLinks: incomingTwgLinks,
		remoteLinks: incomingRemoteLinks,
		loading,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error,
	} = useIncomingLinks(contentId, { contentType });
	// Keep bridge snapshots stable when unrelated byline or AI state changes.
	const incoming = useMemo(
		() => ({
			links: incomingLinks,
			twgLinks: incomingTwgLinks,
			remoteLinks: incomingRemoteLinks,
			loading,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			error,
		}),
		[
			incomingLinks,
			incomingTwgLinks,
			incomingRemoteLinks,
			loading,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			error,
		],
	);
	const {
		enabled,
		links: relatedLinks,
		loading: relatedLoading,
		error: relatedError,
	} = useRelatedContent(contentId, { contentType });
	const relatedContent = useMemo(
		() => ({ enabled, links: relatedLinks, loading: relatedLoading, error: relatedError }),
		[enabled, relatedLinks, relatedLoading, relatedError],
	);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const atAGlance = useAtAGlance({
		enabled: atAGlanceEnabled,
		contentId,
		contentType,
		pageTitle,
		pageAdf: outgoing.collectedForContentId === contentId ? outgoing.collectedAdf : null,
		pageVersion,
		pageUpdatedAt,
		structuralAncestors: connectionAncestors,
		outgoingLinksLoading: outgoing.loading || outgoing.refreshing,
		// useAtAGlance also waits for incoming.loading via appearsIn and for
		// enabled Related content to settle before the first request.
		connections: {
			appearsIn: incoming,
			linksTo: { links: outgoing.links },
			relatedContent,
		},
	});

	// The preview panel mounts outside this subtree and reads the bridge.
	// Publish links with their document snapshot to keep page transitions consistent.
	useEffect(() => {
		publishOutgoingLinks({
			contentId: outgoing.collectedForContentId,
			pageAdf: outgoing.collectedAdf,
			pageVersion: outgoing.collectedForContentId === contentId ? pageVersion : undefined,
			pageUpdatedAt: outgoing.collectedForContentId === contentId ? pageUpdatedAt : undefined,
			structuralAncestors:
				outgoing.collectedForContentId === contentId ? connectionAncestors : undefined,
			outgoingLinks: outgoing.links,
			refreshLinkUrl: outgoing.refreshLinkUrl,
		});
	}, [
		outgoing.collectedForContentId,
		connectionAncestors,
		contentId,
		outgoing.links,
		outgoing.collectedAdf,
		pageUpdatedAt,
		pageVersion,
		outgoing.refreshLinkUrl,
	]);

	// Separate from the publish effect so the store is only emptied when the
	// byline actually goes away, not on every republish.
	useEffect(() => clearOutgoingLinks, []);

	// Tagged with `contentId` rather than a collected-for id because
	// `connectionAncestors` arrives as a prop alongside it, so the two move together.
	useEffect(() => {
		publishDevDocsConnection({ contentId, devDocsConnection });
	}, [contentId, devDocsConnection]);

	useEffect(() => clearDevDocsConnection, []);

	useEffect(() => {
		publishIncomingLinks({ contentId, incomingLinks: incoming });
	}, [contentId, incoming]);
	useEffect(() => clearIncomingLinks, []);

	useEffect(() => {
		publishRelatedContent({ contentId, relatedContent });
	}, [contentId, relatedContent]);
	useEffect(() => clearRelatedContent, []);

	useEffect(() => {
		publishAtAGlance({ contentId, atAGlance });
	}, [contentId, atAGlance]);
	useEffect(() => clearAtAGlance, []);

	return { outgoing, incoming, relatedContent, devDocsConnection, atAGlance };
};
